import React, { useCallback, useMemo } from 'react';
import {
  BuilderChatSideFillIcon,
  BuilderChatSideIcon,
  BuilderExperiencesFill,
  BuilderExperiencesIcon,
  BuilderHomeFillIcon,
  BuilderHomeIcon,
  BuilderInsightsIcon,
  BuilderInternFillIcon,
  BuilderInternIcon,
  BuilderMegaphoneFillIcon,
  BuilderMegaphoneIcon,
  BuilderNineDotGrid,
  BuilderStoreFillIcon,
  BuilderStoreIcon,
  BuilderWalletFillIcon,
  BuilderWalletIcon,
  Button,
  Divider,
  Grid,
  RobloxIcon,
  StudioIcon,
  Typography,
  useMediaQuery,
} from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import RailItem from './RailItem';
import { useRailContext } from '../../layout/providers/RailProvider';
import WorkplaceSelector from '../../components/WorkplaceSelector';
import { clickRailEventModel } from '../../event/eventConstants';
import { TSendEvent } from '../../providers/EventProvider';
import useProductUrls from '../../utils/useProductUrls';
import useScrollStyles from '../../hooks/useScrollStyles';
import { ProductKey, TProductKey } from '../../types';
import useNavigationConfigs from '../../hooks/useNavigationConfigs';
import RailHeader from './RailHeader';
import useRailStyles from './Rail.styles';

type TPrimaryRailContentProps = {
  sendEvent: TSendEvent;
  openStudio: VoidFunction;
  login: VoidFunction;
  pathname: string;
  isAuth: boolean;
};

export const PrimaryRailContent: React.FC<TPrimaryRailContentProps> = ({
  pathname,
  isAuth,
  openStudio,
  sendEvent,
  login,
}) => {
  const {
    cx,
    classes: { railContainer, railContainerCompact, railContainerTransition, labelsRailContainer },
  } = useRailStyles();

  const {
    classes: { scroll },
  } = useScrollStyles();

  const {
    primaryRailCompact,
    drawerVariant,
    allToolsOpen,
    learnOpen,
    isReady,
    setLearnOpen,
    setAllToolsOpen,
    setPrimaryRailOpen,
  } = useRailContext();

  const { currentProduct } = useNavigationConfigs();

  const { translate } = useTranslation();
  const hideStudio = useMediaQuery((theme) => theme.breakpoints.down('Medium'));

  const { Dashboard, Store, Forum, Ads, Roblox } = useProductUrls();

  const selectItem = useCallback(
    (event: string) => {
      sendEvent(clickRailEventModel(event));
      if (drawerVariant === 'temporary' && event !== 'Learn') {
        setPrimaryRailOpen(false);
      }

      if (event === 'Learn') {
        setLearnOpen(!learnOpen);
      } else {
        setLearnOpen(false);
      }
    },
    [drawerVariant, learnOpen, sendEvent, setLearnOpen, setPrimaryRailOpen],
  );

  const active = useMemo(() => {
    if (currentProduct === ProductKey.CreatorDashboard) {
      if (pathname.startsWith(Dashboard.creations)) {
        return 'Creations';
      }

      if (pathname.startsWith(Dashboard.analytics)) {
        return 'Analytics';
      }

      const financesPaths = [
        '/dashboard/devex',
        '/dashboard/transactions',
        '/dashboard/account-information',
        '/dashboard/billing',
        '/dashboard/payments',
        '/dashboard/group/payouts',
      ];

      if (financesPaths.includes(pathname)) {
        return 'Finances';
      }
    }

    const productPaths: TProductKey[] = [
      ProductKey.Home,
      ProductKey.Documentation,
      ProductKey.Store,
      ProductKey.Forum,
    ];

    if (productPaths.includes(currentProduct)) {
      return currentProduct;
    }

    return null;
  }, [Dashboard.analytics, Dashboard.creations, pathname, currentProduct]);

  return (
    <Grid
      classes={{
        root: cx(scroll, railContainer, {
          [railContainerTransition]: isReady,
          [railContainerCompact]: primaryRailCompact,
          [labelsRailContainer]: primaryRailCompact,
        }),
      }}>
      <RailHeader
        compact={primaryRailCompact}
        icon={<RobloxIcon />}
        label={translate('Label.Creator')}
        onClick={() => selectItem('Header')}
        href={Dashboard.home}
      />
      {isAuth && <WorkplaceSelector collapsed={primaryRailCompact} />}
      <RailItem
        disableAnimation={!isReady}
        compact={primaryRailCompact}
        icon={<BuilderHomeIcon />}
        active={active === 'Home'}
        activeIcon={<BuilderHomeFillIcon />}
        label={translate('Heading.Home')}
        onClick={() => selectItem('Home')}
        href={Dashboard.home}
      />
      {isAuth && (
        <RailItem
          disableAnimation={!isReady}
          compact={primaryRailCompact}
          icon={<BuilderExperiencesIcon />}
          activeIcon={<BuilderExperiencesFill />}
          active={active === 'Creations'}
          label={translate('Heading.Creations')}
          onClick={() => selectItem('Creations')}
          href={Dashboard.creations}
        />
      )}
      <RailItem
        disableAnimation={!isReady}
        compact={primaryRailCompact}
        icon={<BuilderInternIcon />}
        activeIcon={<BuilderInternFillIcon />}
        active={active === 'Documentation'}
        label={translate('Heading.Learn')}
        onClick={() => {
          setLearnOpen(true);
          selectItem('Learn');
        }}
      />
      <RailItem
        disableAnimation={!isReady}
        compact={primaryRailCompact}
        icon={<BuilderStoreIcon />}
        activeIcon={<BuilderStoreFillIcon />}
        active={active === 'Store'}
        label={translate('Heading.Store')}
        onClick={() => selectItem('Store')}
        href={Store.home}
      />
      <RailItem
        disableAnimation={!isReady}
        compact={primaryRailCompact}
        icon={<BuilderChatSideIcon />}
        activeIcon={<BuilderChatSideFillIcon />}
        active={active === 'Forum'}
        label={translate('Heading.Forums')}
        onClick={() => selectItem('Forum')}
        href={Forum.home}
      />
      {isAuth && (
        <React.Fragment>
          <Divider flexItem />
          <RailItem
            disableAnimation={!isReady}
            compact={primaryRailCompact}
            icon={<BuilderWalletIcon />}
            activeIcon={<BuilderWalletFillIcon />}
            active={active === 'Finances'}
            label={translate('Heading.Finances')}
            onClick={() => selectItem('Finances')}
            href={Dashboard.finances}
          />
          <RailItem
            disableAnimation={!isReady}
            compact={primaryRailCompact}
            icon={<BuilderInsightsIcon />}
            active={active === 'Analytics'}
            label={translate('Title.Analytics')}
            onClick={() => selectItem('Analytics')}
            href={Dashboard.analytics}
          />
          <RailItem
            disableAnimation={!isReady}
            compact={primaryRailCompact}
            icon={<BuilderMegaphoneIcon />}
            activeIcon={<BuilderMegaphoneFillIcon />}
            label={translate('Heading.Ads')}
            onClick={() => selectItem('Ads')}
            href={Ads.home}
          />
          <Divider flexItem />
        </React.Fragment>
      )}

      {isAuth || primaryRailCompact ? (
        <RailItem
          disableAnimation={!isReady}
          compact={primaryRailCompact}
          icon={<BuilderNineDotGrid />}
          label={translate('Heading.AllTools')}
          onClick={() => {
            if (isAuth) {
              sendEvent(clickRailEventModel('AllTools'));
              setAllToolsOpen(!allToolsOpen);
            } else {
              login();
              sendEvent(clickRailEventModel('AllToolsLogin'));
            }
          }}
        />
      ) : (
        <Button
          variant='contained'
          color='secondary'
          onClick={() => {
            sendEvent(clickRailEventModel('AllToolsLogin'));
            login();
          }}>
          <Typography variant='smallLabel2' noWrap>
            {translate('Action.LogInAllTools')}
          </Typography>
        </Button>
      )}
      <RailItem
        disableAnimation={!isReady}
        bottom
        compact={primaryRailCompact}
        compactStyles='iconOnly'
        icon={<RobloxIcon />}
        label={translate('Label.RobloxWebsite')}
        onClick={() => selectItem('RobloxWebsite')}
        href={Roblox.home}
      />

      {!hideStudio && (
        <RailItem
          disableAnimation={!isReady}
          compactStyles='iconOnly'
          compact={primaryRailCompact}
          icon={<StudioIcon />}
          label={translate('Heading.Studio')}
          onClick={() => {
            selectItem('studio');
            openStudio();
          }}
        />
      )}
    </Grid>
  );
};

export default PrimaryRailContent;
